/**
 * PersonaWizard — Multi-step modal for creating a Persona.
 * Steps: Identity → Vocals → Instruments → Review & Save
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button } from '@components/ui';
import { ArrowLeft, ArrowRight, Check, Users } from 'lucide-react';
import { WizardStepIdentity } from './WizardStepIdentity';
import { WizardStepVocals } from './WizardStepVocals';
import { WizardStepInstruments } from './WizardStepInstruments';
import { WizardStepReview } from './WizardStepReview';
import type { Persona, PersonaIdentity, PersonaIdentityPayload, PersonaWizardData, PersonaWizardStep } from '@/types/persona.types';

const STEPS: PersonaWizardStep[] = ['identity', 'vocals', 'instruments', 'review'];
const STEP_LABELS: Record<PersonaWizardStep, string> = {
  identity: 'Identity',
  vocals: 'Vocals',
  instruments: 'Production',
  review: 'Review & Save',
};

interface PersonaWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PersonaWizardData) => Promise<boolean>;
  pendingPayload: PersonaIdentityPayload | null;
  editingPersona?: Persona | null;
}

export const PersonaWizard: React.FC<PersonaWizardProps> = ({ isOpen, onClose, onSave, pendingPayload, editingPersona }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [wizardData, setWizardData] = useState<PersonaWizardData>({
    identity: null,
    vocalConfigJson: null,
    instrumentConfigJson: null,
    vocalArchitecture: '',
    productionArchitecture: '',
    name: '',
    description: '',
    icon: '🎭',
    tags: '',
  });

  // Initialize from pending payload or editing persona when opening
  useEffect(() => {
    if (!isOpen) {
      // Reset on close
      setStepIdx(0);
      setWizardData({
        identity: null,
        vocalConfigJson: null,
        instrumentConfigJson: null,
        vocalArchitecture: '',
        productionArchitecture: '',
        name: '',
        description: '',
        icon: '🎭',
        tags: '',
      });
      return;
    }

    if (editingPersona) {
      // Edit mode: pre-populate from existing persona, start at vocals step
      let identity: PersonaIdentity | null = null;
      try { identity = JSON.parse(editingPersona.identityJson); } catch { /* use null */ }
      setWizardData({
        identity,
        vocalConfigJson: editingPersona.vocalConfigJson && editingPersona.vocalConfigJson !== '{}' ? editingPersona.vocalConfigJson : null,
        instrumentConfigJson: editingPersona.instrumentConfigJson && editingPersona.instrumentConfigJson !== 'null' ? editingPersona.instrumentConfigJson : null,
        vocalArchitecture: editingPersona.vocalArchitecture || '',
        productionArchitecture: editingPersona.productionArchitecture || '',
        name: editingPersona.name,
        description: editingPersona.description || '',
        icon: editingPersona.icon || '🎭',
        tags: editingPersona.tags || '',
      });
      // Jump to vocals step so they can attach/re-capture configs
      setStepIdx(1);
    } else if (pendingPayload) {
      const identity: PersonaIdentity = {
        source: pendingPayload.source,
        name: pendingPayload.name,
        genre: pendingPayload.genre || '',
        bandBible: pendingPayload.bandBible || null,
        markdownIdentity: pendingPayload.markdown || null,
        styleTags: '',
        excludeTags: '',
      };
      setWizardData(prev => ({
        ...prev,
        identity,
        name: pendingPayload.name,
      }));
    }
  }, [isOpen, pendingPayload, editingPersona]);

  const currentStep = STEPS[stepIdx];
  const isFirstStep = stepIdx === 0;
  const isLastStep = stepIdx === STEPS.length - 1;

  const canProceed = () => {
    if (currentStep === 'identity') return !!wizardData.identity?.name;
    if (currentStep === 'review') return !!wizardData.name.trim();
    return true; // vocals and instruments can be skipped
  };

  const handleNext = () => {
    if (isLastStep) return;
    setStepIdx(prev => prev + 1);
  };

  const handleBack = () => {
    if (isFirstStep) return;
    setStepIdx(prev => prev - 1);
  };

  const handleSave = async () => {
    if (!wizardData.name.trim()) return;
    setIsSaving(true);
    try {
      const success = await onSave(wizardData);
      if (success) onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-accent text-slate-950 shadow-lg shadow-amber-900/20">
            <Users size={20} />
          </div>
          <h2 className="text-2xl font-black text-slate-50 uppercase tracking-tighter">
            {editingPersona ? 'Edit' : 'Persona'} <span className="text-accent">{editingPersona ? 'Persona' : 'Wizard'}</span>
          </h2>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step}>
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest transition-colors ${
                idx === stepIdx ? 'bg-amber-500/20 text-amber-400' :
                idx < stepIdx ? 'text-green-400' : 'text-slate-600'
              }`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                  idx === stepIdx ? 'bg-amber-500 text-black' :
                  idx < stepIdx ? 'bg-green-500/20 text-green-400' : 'bg-slate-800 text-slate-600'
                }`}>
                  {idx < stepIdx ? '✓' : idx + 1}
                </span>
                <span className="hidden sm:inline">{STEP_LABELS[step]}</span>
              </div>
              {idx < STEPS.length - 1 && <div className="w-6 h-px bg-slate-800" />}
            </React.Fragment>
          ))}
        </div>

        {/* Step content */}
        <div className="min-h-[300px]">
          {currentStep === 'identity' && (
            <WizardStepIdentity
              identity={wizardData.identity}
              pendingPayload={pendingPayload}
              onUpdate={identity => setWizardData(prev => ({ ...prev, identity, name: prev.name || identity.name }))}
            />
          )}
          {currentStep === 'vocals' && (
            <WizardStepVocals
              vocalConfigJson={wizardData.vocalConfigJson}
              vocalArchitecture={wizardData.vocalArchitecture}
              onCapture={(json, arch) => setWizardData(prev => ({ ...prev, vocalConfigJson: json, vocalArchitecture: arch }))}
              onSkip={handleNext}
            />
          )}
          {currentStep === 'instruments' && (
            <WizardStepInstruments
              instrumentConfigJson={wizardData.instrumentConfigJson}
              productionArchitecture={wizardData.productionArchitecture}
              onCapture={(json, arch) => setWizardData(prev => ({ ...prev, instrumentConfigJson: json, productionArchitecture: arch }))}
              onSkip={handleNext}
            />
          )}
          {currentStep === 'review' && (
            <WizardStepReview
              data={wizardData}
              onUpdate={fields => setWizardData(prev => ({ ...prev, ...fields }))}
            />
          )}
        </div>

        {/* Navigation */}
        <div className="flex gap-3 pt-2 border-t border-slate-800">
          {!isFirstStep && (
            <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={14} />} onClick={handleBack}>
              Back
            </Button>
          )}
          <div className="flex-1" />
          {isLastStep ? (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Check size={14} />}
              onClick={handleSave}
              disabled={!canProceed() || isSaving}
            >
              {isSaving ? 'Saving...' : editingPersona ? 'Update Persona' : 'Save Persona'}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              rightIcon={<ArrowRight size={14} />}
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
